import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-kit";
import { ClassPicker } from "@/components/ClassPicker";
import { Pager, fmtTime, label } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const TOGGLES = [["approval_required", "Diaries need Principal approval before parents see them"], ["notify_diary_publish", "Notify parents when a diary is published"],
  ["notify_task_assigned", "Notify teachers when the Principal assigns a task"], ["notify_parent_ack", "Notify teachers when a parent marks a diary as seen"],
  ["parents_can_message_teacher", "Parents can message their class teacher"], ["teachers_can_message_parents", "Teachers can message parents of their class"]];

function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/diary-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  async function save() {
    try { setS((await api.put("/diary-settings", { ...s, auto_approve_hours: Number(s.auto_approve_hours) || 0 })).data); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  if (!s) return null;
  return (
    <div data-testid="diary-settings" className="bg-white rounded-2xl shadow-sm p-5 space-y-4 max-w-2xl">
      {TOGGLES.map(([k, l]) => (
        <div key={k} className="flex items-center justify-between gap-4"><Label htmlFor={k} className="font-normal">{l}</Label><Switch id={k} data-testid={`setting-${k}`} checked={!!s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} /></div>
      ))}
      <div className={s.approval_required ? "" : "opacity-50"}>
        <Label>Auto-approve after (hours, 0 = never)</Label>
        <Input data-testid="setting-auto-approve-hours" type="number" min={0} max={168} disabled={!s.approval_required} value={s.auto_approve_hours} onChange={(e) => setS({ ...s, auto_approve_hours: e.target.value })} className="mt-1 w-40" />
        <p className="text-xs text-slate-500 mt-1">If the Principal doesn't approve or reject within this time, the diary is published to parents automatically.</p>
      </div>
      <Button data-testid="settings-save" onClick={save} className="btn-brand">Save settings</Button>
    </div>
  );
}

function AssignmentsPanel() {
  const [rows, setRows] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [cls, setCls] = useState({ branch_id: "", class_name: "", section: "" });
  const [f, setF] = useState({ teacher_id: "", subject: "" });
  const [mode, setMode] = useState("class");
  const [picked, setPicked] = useState([]);
  const load = () => api.get("/teacher-assignments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/staff").then((r) => setTeachers(r.data.filter((u) => u.role === "teacher"))).catch(() => {}); }, []);
  async function add() {
    try {
      await api.post("/teacher-assignments", { ...f, class_name: cls.class_name, section: cls.section, student_ids: mode === "students" ? picked : [] });
      toast.success("Teacher assigned"); setF({ ...f, subject: "" }); setPicked([]); load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function del(id) { try { await api.delete(`/teacher-assignments/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } }
  const options = teachers.filter((t) => !cls.branch_id || t.branch_id === cls.branch_id);
  return (
    <div className="space-y-4">
      <div data-testid="assign-form" className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
        <h3 className="font-display font-bold">Assign a teacher to a class</h3>
        <ClassPicker value={cls} onChange={(v) => { setCls(v); setPicked([]); }} />
        <div className="flex gap-2" data-testid="assign-mode">
          {[["class", "Whole class"], ["students", "Particular students"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`assign-mode-${k}`} onClick={() => setMode(k)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${mode === k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{l}</button>
          ))}
        </div>
        {mode === "students" && <StudentPicker cls={cls} picked={picked} setPicked={setPicked} />}
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div><Label>Teacher</Label>
            <Select value={f.teacher_id} onValueChange={(v) => setF({ ...f, teacher_id: v })}>
              <SelectTrigger data-testid="assign-teacher" className="mt-1"><SelectValue placeholder="Choose teacher" /></SelectTrigger>
              <SelectContent>{options.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Subject (optional)</Label><Input data-testid="assign-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="mt-1" /></div>
          <Button data-testid="assign-save" onClick={add} disabled={!f.teacher_id || !cls.class_name || !cls.section || (mode === "students" && !picked.length)} className="btn-brand">Assign</Button>
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
        {rows.length === 0 && <p className="p-4 text-sm text-slate-500">No assignments yet.</p>}
        {rows.map((a) => (
          <div key={a.id + a.teacher_id} data-testid={`assignment-${a.id}`} className="px-4 py-2.5 flex items-center gap-3 text-sm">
            <span className="font-semibold flex-1">{a.teacher_name}</span>
            <span>{a.class_name} - {a.section}{a.student_names?.length ? <span data-testid={`assignment-students-${a.id}`} title={a.student_names.join(", ")} className="ml-2 text-xs text-violet-700 bg-violet-50 rounded-full px-2 py-0.5">{a.student_names.length === 1 ? a.student_names[0] : `${a.student_names.length} students`}</span> : null}</span><span className="text-slate-500 w-32 truncate">{a.subject || (a.primary ? "Class teacher" : "")}</span>
            {a.primary ? <span className="text-xs text-slate-400 w-8">main</span> : <button data-testid={`assignment-delete-${a.id}`} onClick={() => del(a.id)} className="text-slate-400 hover:text-red-600 w-8"><Trash2 size={15} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function StudentPicker({ cls, picked, setPicked }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!cls.class_name || !cls.section) { setList([]); return; }
    api.get("/students", { params: { class_name: cls.class_name, section: cls.section, branch_id: cls.branch_id || undefined } }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [cls.class_name, cls.section, cls.branch_id]);
  if (!cls.class_name || !cls.section) return <p className="text-xs text-slate-500">Choose a class and section to pick students.</p>;
  const shown = list.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  return (
    <div data-testid="assign-student-picker" className="border border-slate-200 rounded-xl p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="assign-student-search" placeholder="Search student" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-56" />
        <button type="button" data-testid="assign-student-all" onClick={() => setPicked(picked.length === list.length ? [] : list.map((s) => s.id))} className="text-xs text-violet-700 hover:underline">{picked.length === list.length && list.length ? "Clear all" : "Select all"}</button>
        <span data-testid="assign-student-count" className="text-xs text-slate-500 ml-auto">{picked.length} selected</span>
      </div>
      <div className="max-h-48 overflow-y-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-1">
        {list.length === 0 && <p className="text-sm text-slate-500">No students in this class.</p>}
        {shown.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-violet-50 cursor-pointer">
            <input type="checkbox" data-testid={`assign-student-${s.id}`} checked={picked.includes(s.id)} onChange={() => toggle(s.id)} className="accent-violet-600" />
            <span className="truncate">{s.name}</span>{s.roll_number ? <span className="text-xs text-slate-400">#{s.roll_number}</span> : null}
          </label>
        ))}
      </div>
    </div>
  );
}

function AuditPanel() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [d, setD] = useState({ items: [], page: 1, pages: 1, total: 0 });
  useEffect(() => { api.get("/audit-logs", { params: { q, page } }).then((r) => setD(r.data)).catch(() => {}); }, [q, page]);
  return (
    <div>
      <Input data-testid="audit-search" placeholder="Search user, action or detail" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="w-72 bg-white mb-3" />
      <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-violet-50"><tr><th className="text-left px-3 py-2.5">When</th><th className="text-left px-3">User</th><th className="text-left px-3">Action</th><th className="text-left px-3">Detail</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{d.items.map((a) => (
            <tr key={a.id}><td className="px-3 py-2 whitespace-nowrap">{fmtTime(a.created_at)}</td><td className="px-3">{a.user_name}</td><td className="px-3 capitalize">{label(a.action)}</td><td className="px-3 max-w-md truncate">{a.detail}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <Pager page={d.page} pages={d.pages} total={d.total} onPage={setPage} testid="audit-pager" />
    </div>
  );
}

function CoordinatorsPanel() {
  const { user } = useAuth();
  const isAdmin = ["master_admin", "super_admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", branch_id: user?.branch_id || "" });
  const load = () => api.get("/coordinators").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  const bname = (id) => branches.find((b) => b.id === id)?.name || "—";
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="space-y-4">
      <div data-testid="coordinator-form" className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
        <h3 className="font-display font-bold">Create coordinator account</h3>
        <p className="text-xs text-slate-500">Coordinators manage teachers, attendance, tasks, diaries, homework and activities. They never see fees, payments, salaries or other people's private chats.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><Label>Name</Label><Input data-testid="coord-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" /></div>
          <div><Label>Email</Label><Input data-testid="coord-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1" /></div>
          <div><Label>Password (min 8)</Label><Input data-testid="coord-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1" /></div>
          <div><Label>Branch</Label>
            <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })} disabled={!isAdmin}>
              <SelectTrigger data-testid="coord-branch" className="mt-1"><SelectValue placeholder="Choose branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <Button data-testid="coord-save" disabled={!f.name || !f.email || f.password.length < 8 || !f.branch_id} className="btn-brand"
          onClick={() => act(() => api.post("/coordinators", f), "Coordinator created").then(() => setF({ ...f, name: "", email: "", password: "" }))}>Create account</Button>
      </div>
      <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
        {rows.length === 0 && <p className="p-4 text-sm text-slate-500">No coordinators yet.</p>}
        {rows.map((c) => (
          <div key={c.id} data-testid={`coordinator-${c.id}`} className="px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
            <span className="font-semibold">{c.name}</span><span className="text-slate-500 flex-1">{c.email}</span>
            {isAdmin ? (
              <Select value={c.branch_id || ""} onValueChange={(v) => act(() => api.patch(`/coordinators/${c.id}`, { branch_id: v }), "Branch updated")}>
                <SelectTrigger data-testid={`coord-move-${c.id}`} className="w-48 h-9"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : <span>{bname(c.branch_id)}</span>}
            <button data-testid={`coord-delete-${c.id}`} onClick={() => window.confirm(`Remove ${c.name}'s account?`) && act(() => api.delete(`/coordinators/${c.id}`), "Removed")} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DiaryAdminPage() {
  const { user } = useAuth();
  const isAdmin = ["master_admin", "super_admin"].includes(user?.role);
  const canCoord = isAdmin || ["principal", "center_head"].includes(user?.role);
  return (
    <AppShell>
      <PageHeader eyebrow="Admin" title="Diary & Communication Setup" subtitle="Teacher class assignments, diary approval, notification and messaging permissions, audit log." />
      <Tabs defaultValue="assign">
        <TabsList className="bg-violet-100 flex-wrap h-auto">
          <TabsTrigger value="assign" data-testid="admin-tab-assign">Teacher assignments</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="admin-tab-settings">Approval & notifications</TabsTrigger>}
          {canCoord && <TabsTrigger value="coordinators" data-testid="admin-tab-coordinators">Coordinators</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="assign" className="mt-4"><AssignmentsPanel /></TabsContent>
        {isAdmin && <TabsContent value="settings" className="mt-4"><SettingsPanel /></TabsContent>}
        {canCoord && <TabsContent value="coordinators" className="mt-4"><CoordinatorsPanel /></TabsContent>}
        <TabsContent value="audit" className="mt-4"><AuditPanel /></TabsContent>
      </Tabs>
    </AppShell>
  );
}
